import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { X, Plus, Play, Pause, ChevronRight, CalendarClock, Ban, Copy, Zap, Infinity } from 'lucide-react';
import * as api from '../api';

const FREQ_LABELS = {
  ponctuel:     'Ponctuel',
  quotidien:    'Tous les jours',
  hebdomadaire: 'Hebdomadaire',
  quinzaine:    'Tous les 15 jours',
  mensuel:      'Mensuel',
  trimestriel:  'Trimestriel',
  semestriel:   'Semestriel',
  annuel:       'Annuel',
  personnalise: 'Personnalisé (N jours)',
};

// JS weekday : 0=Dim, 1=Lun … 6=Sam — on propose Lun(1) à Sam(6) uniquement
const JOURS_SEMAINE = [
  { value: '1', label: 'Lundi' },
  { value: '2', label: 'Mardi' },
  { value: '3', label: 'Mercredi' },
  { value: '4', label: 'Jeudi' },
  { value: '5', label: 'Vendredi' },
  { value: '6', label: 'Samedi' },
];

const STATUS_CFG = {
  draft:     { label: 'Brouillon',  bg: '#F1F5F9', color: '#64748B' },
  scheduled: { label: 'Planifiée',  bg: '#DBEAFE', color: '#1D4ED8' },
  sending:   { label: 'En cours',   bg: '#FEF9C3', color: '#B45309' },
  paused:    { label: 'En pause',   bg: '#FEF3C7', color: '#D97706' },
  completed: { label: 'Terminée',   bg: '#DCFCE7', color: '#16A34A' },
  cancelled: { label: 'Annulée',    bg: '#FEE2E2', color: '#DC2626' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CFG[status?.toLowerCase()] || STATUS_CFG.draft;
  return (
    <span style={{ background: cfg.bg, color: cfg.color, borderRadius: 99,
      padding: '2px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
};

// ─── Convertit un ISO UTC en valeur pour datetime-local ──────────────────────
// Utilise la timezone société si disponible (évite les décalages navigateur)
function toLocalInput(iso, tz) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  if (tz) {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(d);
      const get = type => parts.find(p => p.type === type)?.value || '00';
      return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
    } catch {}
  }
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ─── Calcule la prochaine occurrence pour une campagne récurrente ─────────────
function buildScheduledAt(frequence, jour, heureStr, opts = {}) {
  const { jourSemaine = '1', frequenceJours = '7', reporterWeekend = 'lundi' } = opts;
  const [h, m] = (heureStr || '08:00').split(':').map(n => parseInt(n) || 0);
  const now = new Date();

  /** Décale d une Date si elle tombe un samedi ou dimanche */
  function _eviterWeekend(dt, mode) {
    if (mode === 'aucun') return dt;
    const day = dt.getDay(); // 0=Dim, 6=Sam
    if (day === 0) return new Date(dt.getTime() + (mode === 'lundi' ? 1 : -2) * 86400000);
    if (day === 6) return new Date(dt.getTime() + (mode === 'lundi' ? 2 : -1) * 86400000);
    return dt;
  }

  if (frequence === 'quotidien') {
    const dt = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, h, m, 0);
    return fmtDatetimeLocal(dt);
  }

  if (frequence === 'mensuel') {    const j = parseInt(jour) || 1;
    let dt = new Date(now.getFullYear(), now.getMonth(), j, h, m, 0);
    if (dt <= now) dt = new Date(now.getFullYear(), now.getMonth() + 1, j, h, m, 0);
    dt = _eviterWeekend(dt, reporterWeekend);
    return fmtDatetimeLocal(dt);
  }

  if (frequence === 'hebdomadaire' || frequence === 'quinzaine') {
    const targetDay = parseInt(jourSemaine) || 1; // 1=Lun … 6=Sam (même valeur que JS)
    const today = now.getDay() || 7; // convertit 0(Dim)→7 pour tri Lun=1
    const target = targetDay === 0 ? 7 : targetDay;
    let daysUntil = (target - today + 7) % 7;
    if (daysUntil === 0) daysUntil = 7; // toujours dans le futur
    const dt = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysUntil, h, m, 0);
    return fmtDatetimeLocal(dt);
  }

  if (frequence === 'personnalise') {
    const n = Math.max(1, parseInt(frequenceJours) || 7);
    const dt = new Date(now.getFullYear(), now.getMonth(), now.getDate() + n, h, m, 0);
    return fmtDatetimeLocal(dt);
  }

  // trimestriel / semestriel / annuel : prochain jour ouvré à l'heure donnée
  let dt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0);
  if (dt <= now) dt = new Date(dt.getTime() + 86400000);
  dt = _eviterWeekend(dt, reporterWeekend);
  return fmtDatetimeLocal(dt);
}

function fmtDatetimeLocal(dt) {
  const pad = n => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

// ─── Formattage date avec "à" entre date et heure ────────────────────────────
function fmtDateAt(iso, tz) {
  if (!iso) return '—';
  const d = new Date(iso);
  const opts = { day: '2-digit', month: '2-digit', year: 'numeric' };
  const optsH = { hour: '2-digit', minute: '2-digit' };
  if (tz) { opts.timeZone = tz; optsH.timeZone = tz; }
  return `${d.toLocaleDateString('fr-FR', opts)} à ${d.toLocaleTimeString('fr-FR', optsH)}`;
}

function fmtDate(iso, tz) {
  if (!iso) return '—';
  const opts = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
  if (tz) opts.timeZone = tz;
  return new Date(iso).toLocaleString('fr-FR', opts);
}

// ─── Formulaire (utilisé dans le side panel) ─────────────────────────────────
function CampagneForm({ initial, onSave, onDelete, onCancel, onDuplicate, onSendNow, templates, audiences, agences, societes, fmtDateDisp }) {
  const isNew  = !initial?.campaign_id;
  const status = (initial?.send_status || 'draft').toLowerCase();
  const tz     = initial?.societe_timezone || '';

  const [form, setForm] = useState({
    label:                initial?.label          || '',
    actif:                initial?.actif          ?? true,
    mailing_type:         initial?.mailing_type   || 'General',
    frequence:            initial?.frequence      || 'mensuel',
    scheduled_at:         initial?.scheduled_at   ? toLocalInput(initial.scheduled_at, tz) : '',
    jour_envoi:           String(initial?.config?.jour_envoi  || '1'),
    heure_envoi:          initial?.config?.heure_envoi        || '08:00',
    duree_renouvellement: String(initial?.config?.duree_renouvellement || '12'),
    duree_indefinie:      initial?.config?.duree_indefinie    ?? false,
    jour_semaine:         String(initial?.config?.jour_semaine ?? '1'),
    frequence_jours:      String(initial?.config?.frequence_jours || '7'),
    reporter_weekend:     initial?.config?.reporter_weekend   || 'lundi',
    template_id:          initial?.template_id    || '',
    audience_id:          initial?.audience_id    || '',
    agence_id:            initial?.config?.agence_id || '',
    nom_expediteur:       initial?.nom_expediteur || '',
    email_reponse:        initial?.email_reponse  || '',
  });
  const [saving, setSaving]         = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const isRecurrent  = form.frequence !== 'ponctuel';
  const isRelance    = form.mailing_type === 'Relance';

  const handleSendNow = async () => {
    setSendingNow(true);
    try {
      await onSendNow(initial.campaign_id);
    } finally {
      setSendingNow(false);
    }
  };

  const handleSave = async () => {    if (!form.label.trim()) { toast.error('Libellé obligatoire'); return; }

    // Calcul du scheduled_at pour les campagnes récurrentes
    let scheduledAtToSend = form.scheduled_at;
    if (isRecurrent && form.heure_envoi) {
      scheduledAtToSend = buildScheduledAt(form.frequence, form.jour_envoi, form.heure_envoi, {
        jourSemaine:     form.jour_semaine,
        frequenceJours:  form.frequence_jours,
        reporterWeekend: form.reporter_weekend,
      });
    }

    setSaving(true);
    try {
      await onSave({
        ...form,
        scheduled_at: scheduledAtToSend,
        config: {
          agence_id: form.agence_id || null,
          ...(isRecurrent && {
            heure_envoi:          form.heure_envoi  || undefined,
            duree_indefinie:      form.duree_indefinie || undefined,
            ...(!form.duree_indefinie && {
              duree_renouvellement: parseInt(form.duree_renouvellement) || undefined,
            }),
            // mensuel
            ...(form.frequence === 'mensuel' && { jour_envoi: parseInt(form.jour_envoi) || undefined }),
            // hebdomadaire / quinzaine
            ...(['hebdomadaire','quinzaine'].includes(form.frequence) && { jour_semaine: parseInt(form.jour_semaine) || undefined }),
            // personnalisé
            ...(form.frequence === 'personnalise' && { frequence_jours: parseInt(form.frequence_jours) || undefined }),
            // reporter weekend (mensuel / trimestriel / semestriel / annuel)
            ...(!['hebdomadaire','quinzaine','personnalise','ponctuel','quotidien'].includes(form.frequence) && {
              reporter_weekend: form.reporter_weekend,
            }),
          }),
        },
      }, initial);
    } finally { setSaving(false); }
  };

  const inp  = { width: '100%', padding: '7px 10px', border: '1px solid var(--border)',
    borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: '#fff' };
  const sel  = { ...inp };
  const lbl  = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 };
  const row3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 };
  const full = { marginBottom: 14 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

        {/* Libellé */}
        <div style={full}>
          <label style={lbl}>Libellé *</label>
          <input value={form.label} onChange={e => set('label', e.target.value)}
            placeholder="Rapport mensuel clients" style={inp} data-testid="campagne-label" />
        </div>

        {/* Type de campagne */}
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Type de campagne</label>
          <select value={form.mailing_type} onChange={e => set('mailing_type', e.target.value)} style={sel}
            data-testid="campagne-mailing-type">
            <option value="General">Général</option>
            <option value="Relance">Relance</option>
          </select>
        </div>

        {/* Modèle + Audience (avant Planification) */}
        <div style={row2}>
          <div>
            <label style={lbl}>Modèle d'email</label>
            <select value={form.template_id} onChange={e => set('template_id', e.target.value)} style={sel}>
              <option value="">— Aucun —</option>
              {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.nom}</option>)}
            </select>
          </div>
          {!isRelance && (
            <div>
              <label style={lbl}>Audience</label>
              <select value={form.audience_id} onChange={e => set('audience_id', e.target.value)} style={sel}
                data-testid="campagne-audience">
                <option value="">— Abonnés directs —</option>
                {audiences.map(a => (
                  <option key={a.audience_id} value={a.audience_id}>{a.audience_name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Section Planification */}
        <div style={{ marginBottom: 10 }}>
          <strong style={{ fontSize: 13, color: '#1E293B' }}>Planification</strong>
        </div>

        {/* Fréquence */}
        <div style={full}>
          <label style={lbl}>Fréquence</label>
          <select value={form.frequence} onChange={e => set('frequence', e.target.value)} style={sel}
            data-testid="campagne-frequence">
            {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        {/* Champs planification conditionnels */}
        {form.frequence === 'ponctuel' ? (
          <div style={full}>
            <label style={lbl}>Date et heure</label>
            <input type="datetime-local" value={form.scheduled_at}
              onChange={e => set('scheduled_at', e.target.value)}
              style={inp} data-testid="campagne-scheduled-at" />
          </div>
        ) : (
          <>
            {/* Ligne 1 : champ contextuel selon fréquence + heure */}
            <div style={row2}>
              <div>
                {form.frequence === 'mensuel' && (
                  <>
                    <label style={lbl}>Jour du mois</label>
                    <select value={form.jour_envoi} onChange={e => set('jour_envoi', e.target.value)}
                      style={sel} data-testid="campagne-jour-envoi">
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </>
                )}
                {(form.frequence === 'hebdomadaire' || form.frequence === 'quinzaine') && (
                  <>
                    <label style={lbl}>Jour de la semaine</label>
                    <select value={form.jour_semaine} onChange={e => set('jour_semaine', e.target.value)}
                      style={sel} data-testid="campagne-jour-semaine">
                      {JOURS_SEMAINE.map(j => <option key={j.value} value={j.value}>{j.label}</option>)}
                    </select>
                  </>
                )}
                {form.frequence === 'personnalise' && (
                  <>
                    <label style={lbl}>Tous les (jours)</label>
                    <input type="number" min="1" max="365" value={form.frequence_jours}
                      onChange={e => set('frequence_jours', e.target.value)}
                      style={inp} data-testid="campagne-frequence-jours" />
                  </>
                )}
                {['trimestriel','semestriel','annuel'].includes(form.frequence) && (
                  <>
                    <label style={lbl}>Heure d'envoi</label>
                    <input type="time" value={form.heure_envoi}
                      onChange={e => set('heure_envoi', e.target.value)}
                      style={inp} data-testid="campagne-heure-envoi" />
                  </>
                )}
              </div>
              <div>
                <label style={lbl}>
                  {form.frequence === 'mensuel'      ? 'Heure d\'envoi' :
                   form.frequence === 'personnalise' ? 'Heure d\'envoi' :
                   (form.frequence === 'hebdomadaire' || form.frequence === 'quinzaine') ? 'Heure d\'envoi' :
                   /* trimestriel/semestriel/annuel already shown above */ 'Durée'}
                </label>
                <input type="time" value={form.heure_envoi}
                  onChange={e => set('heure_envoi', e.target.value)}
                  style={inp} data-testid="campagne-heure-envoi" />
              </div>
            </div>

            {/* Ligne 2 : Reporter week-end (mensuel / trimestriel / semestriel / annuel) */}
            {!['hebdomadaire','quinzaine','personnalise','quotidien'].includes(form.frequence) && (
              <div style={{ ...full, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={lbl}>Si week-end</label>
                  <select value={form.reporter_weekend} onChange={e => set('reporter_weekend', e.target.value)}
                    style={sel} data-testid="campagne-reporter-weekend">
                    <option value="aucun">Envoyer quand même</option>
                    <option value="lundi">Reporter au lundi suivant</option>
                    <option value="vendredi">Avancer au vendredi précédent</option>
                  </select>
                </div>
                <div>
                  {!form.duree_indefinie ? (
                    <>
                      <label style={lbl}>
                        {form.frequence === 'mensuel'    ? 'Durée (mois)' :
                         form.frequence === 'trimestriel' ? 'Durée (trimestres)' :
                         form.frequence === 'semestriel'  ? 'Durée (semestres)' :
                         'Durée (années)'}
                      </label>
                      <input type="number" min="1" max="120" value={form.duree_renouvellement}
                        onChange={e => set('duree_renouvellement', e.target.value)}
                        style={{ ...inp, marginBottom: 6 }} data-testid="campagne-duree" />
                    </>
                  ) : (
                    <label style={lbl}>Durée</label>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                    fontSize: 12, color: form.duree_indefinie ? '#2563EB' : '#6B7280',
                    fontWeight: form.duree_indefinie ? 600 : 400, marginTop: form.duree_indefinie ? 22 : 0 }}>
                    <input type="checkbox" checked={form.duree_indefinie}
                      onChange={e => set('duree_indefinie', e.target.checked)}
                      data-testid="campagne-duree-indefinie" />
                    <Infinity size={13} /> Sans fin
                  </label>
                </div>
              </div>
            )}

            {/* Durée pour hebdo / quinzaine / personnalise */}
            {['hebdomadaire','quinzaine','personnalise'].includes(form.frequence) && (
              <div style={full}>
                <label style={lbl}>
                  {form.frequence === 'personnalise' ? 'Durée (occurrences)' : 'Durée (semaines)'}
                </label>
                {!form.duree_indefinie && (
                  <input type="number" min="1" max="520" value={form.duree_renouvellement}
                    onChange={e => set('duree_renouvellement', e.target.value)}
                    style={{ ...inp, marginBottom: 6 }} data-testid="campagne-duree" />
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                  fontSize: 12, color: form.duree_indefinie ? '#2563EB' : '#6B7280', fontWeight: form.duree_indefinie ? 600 : 400 }}>
                  <input type="checkbox" checked={form.duree_indefinie}
                    onChange={e => set('duree_indefinie', e.target.checked)}
                    data-testid="campagne-duree-indefinie" />
                  <Infinity size={13} /> Sans fin (re-planifie automatiquement)
                </label>
              </div>
            )}

            {/* Durée pour quotidien */}
            {form.frequence === 'quotidien' && (
              <div style={full}>
                <label style={lbl}>Durée d'exécution</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                  fontSize: 12, color: form.duree_indefinie ? '#2563EB' : '#6B7280', fontWeight: form.duree_indefinie ? 600 : 400 }}>
                  <input type="checkbox" checked={form.duree_indefinie}
                    onChange={e => set('duree_indefinie', e.target.checked)}
                    data-testid="campagne-duree-indefinie" />
                  <Infinity size={13} /> Sans fin (re-planifie chaque jour automatiquement)
                </label>
              </div>
            )}
          </>
        )}

        {/* Expéditeur (Société ou Agence) */}
        <div style={full}>
          <label style={lbl}>Expéditeur (Société ou Agence)</label>
          <select value={form.agence_id} onChange={e => set('agence_id', e.target.value)} style={sel}
            data-testid="campagne-expediteur">
            <option value="">— Non défini —</option>
            {societes && societes.length > 0 && (
              <optgroup label="Sociétés">
                {societes.map(s => (
                  <option key={s.societe_id} value={s.societe_id}>{s.nom}</option>
                ))}
              </optgroup>
            )}
            {agences && agences.length > 0 && (
              <optgroup label="Agences">
                {agences.map(a => <option key={a.agence_id} value={a.agence_id}>{a.nom}</option>)}
              </optgroup>
            )}
          </select>
        </div>

        {/* Expéditeur + Réponse */}
        <div style={row2}>
          <div>
            <label style={lbl}>Nom expéditeur</label>
            <input value={form.nom_expediteur} onChange={e => set('nom_expediteur', e.target.value)}
              placeholder="Mon Agence" style={inp} />
          </div>
          <div>
            <label style={lbl}>Email de réponse</label>
            <input type="email" value={form.email_reponse} onChange={e => set('email_reponse', e.target.value)}
              placeholder="contact@agence.fr" style={inp} />
          </div>
        </div>

        {/* Info statut (si non brouillon) */}
        {!isNew && status !== 'draft' && (
          <div style={{ background: '#F8FAFC', borderRadius: 8, padding: '10px 14px',
            fontSize: 12, color: '#64748B', marginTop: 8 }}>
            <strong>Statut :</strong> <StatusBadge status={status} />
            {initial?.scheduled_at && status === 'scheduled' && (
              <span style={{ marginLeft: 12 }}>
                Planifiée le {fmtDateAt(initial.scheduled_at, tz)}
              </span>
            )}
            {initial?.sent_at && (
              <span style={{ marginLeft: 12 }}>
                Envoyée le {fmtDateAt(initial.sent_at, tz)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Pied fixe */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '14px 24px',
        display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>

        {/* Actions état — côté gauche */}
        <div style={{ flex: 1, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {/* Envoyer maintenant — toujours visible si pas en cours */}
          {!isNew && status !== 'sending' && (
            <button onClick={handleSendNow} disabled={sendingNow}
              data-testid="campagne-send-now-btn"
              style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6,
                border: '1px solid #BBF7D0', background: sendingNow ? '#F0FDF4' : '#ECFDF5',
                color: '#16A34A', cursor: sendingNow ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
              <Zap size={12} /> {sendingNow ? 'Envoi…' : 'Envoyer maintenant'}
            </button>
          )}
          {!isNew && status === 'scheduled' && (
            <button onClick={() => onCancel('cancel', initial.campaign_id)}
              style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #FCA5A5',
                background: '#FEF2F2', color: '#DC2626', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Ban size={12} /> Annuler la planif.
            </button>
          )}
          {!isNew && status === 'sending' && (
            <button onClick={() => onCancel('pause', initial.campaign_id)}
              style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #FCD34D',
                background: '#FFFBEB', color: '#D97706', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Pause size={12} /> Pauser l'envoi
            </button>
          )}
          {!isNew && status === 'paused' && (
            <button onClick={() => onCancel('resume', initial.campaign_id)}
              style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #6EE7B7',
                background: '#ECFDF5', color: '#059669', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Play size={12} /> Reprendre
            </button>
          )}
          {!isNew && (
            <button onClick={() => onDuplicate(initial)}
              data-testid="campagne-dupliquer-btn"
              style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)',
                background: '#fff', color: '#64748B', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Copy size={12} /> Dupliquer
            </button>
          )}
          {!isNew && (
            <button onClick={() => onCancel('delete', initial.campaign_id)}
              style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)',
                background: '#fff', color: '#9CA3AF', cursor: 'pointer' }}>
              Supprimer
            </button>
          )}
        </div>

        {/* Enregistrer */}
        <button onClick={handleSave} disabled={saving} data-testid="campagne-save-btn"
          style={{ padding: '7px 20px', borderRadius: 7, border: 'none',
            background: saving ? '#93C5FD' : '#2563EB', color: '#fff',
            fontWeight: 600, fontSize: 13, cursor: saving ? 'default' : 'pointer' }}>
          {saving ? 'Enregistrement…' : (isNew ? 'Créer' : 'Enregistrer')}
        </button>
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function EmailCampagnes() {
  const [campagnes,  setCampagnes]  = useState([]);
  const [templates,  setTemplates]  = useState([]);
  const [audiences,  setAudiences]  = useState([]);
  const [agences,    setAgences]    = useState([]);
  const [societes,   setSocietes]   = useState([]);
  const [sidePanel,  setSidePanel]  = useState(null);   // null | { mode:'new'|'edit', data }
  const [loading,    setLoading]    = useState(true);
  const [toggling,   setToggling]   = useState(null);   // id en cours de toggle

  const load = useCallback(async () => {
    try {
      const [c, t, a, ag, soc] = await Promise.all([
        api.getMailingTypes(),
        api.getCampagneTemplates(),
        api.getEmailAudiences(),
        api.getAgences(),
        api.getSocietes(),
      ]);
      setCampagnes(c.data || []);
      setTemplates(t.data || []);
      setAudiences(a.data || []);
      setAgences(ag.data || []);
      setSocietes(soc.data || []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Helpers lookup ─────────────────────────────────────────────────────────
  const expediteurNom = id => {
    if (!id) return '—';
    if (id.startsWith('SOC-')) return societes.find(s => s.societe_id === id)?.nom || id;
    return agences.find(a => a.agence_id === id)?.nom || '—';
  };
  const audienceNom = id => audiences.find(a => a.audience_id === id)?.audience_name || 'Abonnés directs';

  // ── Toggle actif ──────────────────────────────────────────────────────────
  const handleToggleActif = async (e, camp) => {
    e.stopPropagation();
    setToggling(camp.campaign_id);
    try {
      await api.toggleCampagneActif(camp.campaign_id);
      setCampagnes(prev => prev.map(c =>
        c.campaign_id === camp.campaign_id ? { ...c, actif: !c.actif } : c
      ));
    } catch { toast.error('Erreur lors du changement d\'état'); }
    finally { setToggling(null); }
  };

  // ── Enregistrer (create ou update + scheduling) ───────────────────────────
  const handleSave = async (form, original) => {
    const payload = {
      label:          form.label,
      actif:          form.actif,
      mailing_type:   form.mailing_type || 'General',
      frequence:      form.frequence,
      template_id:    form.template_id || null,
      audience_id:    form.audience_id || null,
      nom_expediteur: form.nom_expediteur,
      email_reponse:  form.email_reponse,
      config:         form.config || {},
    };

    try {
      if (!original?.campaign_id) {
        // ── Création ──────────────────────────────────────────────────────
        const res = await api.createMailingType(payload);
        const newId = res.data?.campaign_id;
        if (form.scheduled_at && newId) {
          await api.scheduleCampagne(newId, form.scheduled_at);
        }
        toast.success('Campagne créée');
      } else {
        // ── Mise à jour ───────────────────────────────────────────────────
        const mid    = original.campaign_id;
        const status = (original.send_status || 'draft').toLowerCase();
        await api.updateMailingType(mid, payload);

        const newDate  = form.scheduled_at;
        const origDate = original.scheduled_at ? toLocalInput(original.scheduled_at, original.societe_timezone) : '';

        if (newDate) {
          if (['draft', 'cancelled', 'paused'].includes(status)) {
            await api.scheduleCampagne(mid, newDate);
          } else if (status === 'scheduled' && newDate !== origDate) {
            await api.cancelCampagne(mid);
            await api.scheduleCampagne(mid, newDate);
          }
        } else if (!newDate && status === 'scheduled') {
          await api.cancelCampagne(mid);
        }
        toast.success('Campagne enregistrée');
      }
      setSidePanel(null);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur';
      toast.error(msg);
      throw err;
    }
  };

  // ── Dupliquer ─────────────────────────────────────────────────────────────
  const handleDuplicate = async (original) => {
    const payload = {
      label:          `Copie de ${original.label}`,
      actif:          original.actif,
      mailing_type:   original.mailing_type || 'General',
      frequence:      original.frequence,
      template_id:    original.template_id || null,
      audience_id:    original.audience_id || null,
      nom_expediteur: original.nom_expediteur || '',
      email_reponse:  original.email_reponse  || '',
      config:         original.config || {},
    };
    try {
      const res   = await api.createMailingType(payload);
      const newId = res.data?.campaign_id;
      toast.success('Campagne dupliquée');
      const updated = await api.getMailingTypes();
      const list    = updated.data || [];
      const newCamp = list.find(c => c.campaign_id === newId);
      setSidePanel({ mode: 'edit', data: newCamp || null });
      setCampagnes(list);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la duplication');
    }
  };

  // ── Envoyer maintenant ────────────────────────────────────────────────────
  const handleSendNow = async (mid) => {
    try {
      await api.sendNowCampagne(mid);
      toast.success('Envoi démarré en arrière-plan — statut mis à jour dans quelques secondes');
      setTimeout(async () => {
        const updated = await api.getMailingTypes();
        const camp = (updated.data || []).find(c => c.campaign_id === mid);
        if (camp) setSidePanel(prev => prev ? { ...prev, data: camp } : null);
        load();
      }, 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors du lancement');
    }
  };

  // ── Actions état machine depuis le side panel ─────────────────────────────
  const handleAction = async (action, mid) => {    try {
      if (action === 'cancel')  await api.cancelCampagne(mid);
      if (action === 'pause')   await api.pauseCampagne(mid);
      if (action === 'resume')  await api.resumeCampagne(mid);
      if (action === 'delete') {
        if (!window.confirm('Supprimer cette campagne ?')) return;
        await api.deleteMailingType(mid);
        setSidePanel(null);
        toast.success('Campagne supprimée');
        load();
        return;
      }
      toast.success('Action effectuée');
      const updated = await api.getMailingTypes();
      const camp = (updated.data || []).find(c => c.campaign_id === mid);
      if (camp) setSidePanel({ mode: 'edit', data: camp });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const th = {
    padding: '9px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.06em', color: '#6B7280', background: '#F9FAFB',
    borderBottom: '1px solid var(--border)', textAlign: 'left', whiteSpace: 'nowrap',
  };
  const td = {
    padding: '11px 14px', fontSize: 13, borderBottom: '1px solid var(--border)',
    color: '#1E293B', verticalAlign: 'middle',
  };

  return (
    <div style={{ padding: '24px 28px' }}>

      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Campagnes email</h2>
        <button onClick={() => setSidePanel({ mode: 'new', data: null })}
          data-testid="new-campagne-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px',
            background: '#2563EB', color: '#fff', border: 'none', borderRadius: 7,
            fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          <Plus size={15} /> Nouvelle campagne
        </button>
      </div>

      {/* Tableau */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid var(--border)',
        overflow: 'hidden', overflowX: 'auto' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Chargement…</div>
        ) : campagnes.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>
            <CalendarClock size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>Aucune campagne. Créez-en une pour commencer.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Expéditeur</th>
                <th style={th}>Libellé</th>
                <th style={th}>Type</th>
                <th style={th}>Audience</th>
                <th style={th}>Fréquence</th>
                <th style={th}>Planification</th>
                <th style={th}>Envoi effectif</th>
                <th style={th}>Statut</th>
                <th style={{ ...th, textAlign: 'center' }}>Actif</th>
              </tr>
            </thead>
            <tbody>
              {campagnes.map(c => (
                <tr key={c.campaign_id}
                  onClick={() => setSidePanel({ mode: 'edit', data: c })}
                  data-testid={`campagne-row-${c.campaign_id}`}
                  style={{ cursor: 'pointer', transition: 'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>

                  <td style={{ ...td, color: '#64748B' }}>{expediteurNom(c.config?.agence_id)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{c.label}</td>
                  <td style={td}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
                      background: c.mailing_type === 'Relance' ? '#FEF3C7' : '#EFF6FF',
                      color:      c.mailing_type === 'Relance' ? '#92400E' : '#1E40AF',
                    }}>{c.mailing_type === 'Relance' ? 'Relance' : 'Général'}</span>
                  </td>
                  <td style={td}>{c.mailing_type !== 'Relance' ? audienceNom(c.audience_id) : '—'}</td>
                  <td style={td}>{FREQ_LABELS[c.frequence] || c.frequence || '—'}</td>
                  <td style={td}>
                    {c.frequence !== 'ponctuel' && c.config?.heure_envoi ? (
                      <span style={{ fontSize: 12, color: '#64748B' }}>
                        {c.frequence === 'mensuel' && c.config.jour_envoi
                          ? `Le ${c.config.jour_envoi} à ${c.config.heure_envoi}`
                          : `à ${c.config.heure_envoi}`}
                      </span>
                    ) : c.scheduled_at ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 5,
                        color: c.send_status === 'scheduled' ? '#1D4ED8' : '#64748B', fontSize: 12 }}>
                        <CalendarClock size={12} />
                        {fmtDateAt(c.scheduled_at, c.societe_timezone)}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ ...td, fontSize: 12, color: c.sent_at ? '#16A34A' : '#94A3B8' }}>
                    {c.sent_at ? fmtDateAt(c.sent_at, c.societe_timezone) : '—'}
                  </td>
                  <td style={td}><StatusBadge status={c.send_status} /></td>
                  <td style={{ ...td, textAlign: 'center' }}
                    onClick={e => handleToggleActif(e, c)}>
                    <button
                      data-testid={`campagne-toggle-${c.campaign_id}`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer',
                        opacity: toggling === c.campaign_id ? 0.4 : 1,
                        color: c.actif ? '#16A34A' : '#D1D5DB', transition: 'color 0.15s' }}
                      title={c.actif ? 'Activer / Désactiver' : 'Activer'}>
                      {c.actif
                        ? <Play size={18} fill="#16A34A" />
                        : <Play size={18} />
                      }
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Overlay + Side panel */}
      {sidePanel && (
        <>
          {/* Overlay */}
          <div onClick={() => setSidePanel(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 999 }} />

          {/* Panel — largeur augmentée de 50% (480 → 720px) */}
          <div style={{
            position: 'fixed', top: 0, right: 0,
            width: 720, height: '100vh',
            background: '#fff', zIndex: 1000,
            boxShadow: '-6px 0 32px rgba(0,0,0,0.12)',
            display: 'flex', flexDirection: 'column',
            animation: 'slideInRight 0.2s ease',
          }}>
            {/* Header */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <ChevronRight size={16} color="#2563EB" />
                <span style={{ fontWeight: 700, fontSize: 15 }}>
                  {sidePanel.mode === 'new' ? 'Nouvelle campagne' : (sidePanel.data?.label || 'Campagne')}
                </span>
                {sidePanel.data?.send_status && <StatusBadge status={sidePanel.data.send_status} />}
              </div>
              <button onClick={() => setSidePanel(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  color: '#9CA3AF', borderRadius: 6, padding: 4 }}>
                <X size={18} />
              </button>
            </div>

            {/* Formulaire */}
            <CampagneForm
              key={sidePanel.data?.campaign_id || 'new'}
              initial={sidePanel.data}
              onSave={handleSave}
              onDelete={id => handleAction('delete', id)}
              onCancel={handleAction}
              onDuplicate={handleDuplicate}
              onSendNow={handleSendNow}
              templates={templates}
              audiences={audiences}
              agences={agences}
              societes={societes}
              fmtDateDisp={fmtDate}
            />
          </div>
        </>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
